// Sjåførens fakturaopplysninger (users/{uid}.invoiceProfile): validering og normalisering.

const ORG_WEIGHTS = [3, 2, 7, 6, 5, 4, 3, 2];
const ACCOUNT_WEIGHTS = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];

// Samme grenser som Firestore-reglene.
export const INVOICE_PROFILE_LIMITS = { legalName: 120, street: 120, city: 80 };

export const EMPTY_INVOICE_PROFILE = {
  legalName: '',
  orgNumber: '',
  mvaRegistered: false,
  bankAccount: '',
  invoiceAddress: { street: '', postalCode: '', city: '' },
};

export function cleanDigits(value) {
  return String(value ?? '').replace(/[\s.-]/g, '');
}

// MOD11-kontrollsiffer. Gir null når resten gir kontrollsiffer 10 (ugyldig nummer).
function checkDigit(digits, weights) {
  const sum = weights.reduce((s, w, i) => s + w * Number(digits[i]), 0);
  const rest = sum % 11;
  if (rest === 0) return 0;
  const check = 11 - rest;
  return check === 10 ? null : check;
}

export function isValidOrgNumber(value) {
  const d = cleanDigits(value);
  return /^\d{9}$/.test(d) && checkDigit(d, ORG_WEIGHTS) === Number(d[8]);
}

export function isValidBankAccount(value) {
  const d = cleanDigits(value);
  return /^\d{11}$/.test(d) && checkDigit(d, ACCOUNT_WEIGHTS) === Number(d[10]);
}

// Det som lagres i Firestore: kun siffer i nummerfeltene og trimmet tekst.
export function normalizeInvoiceProfile(profile) {
  const p = profile ?? {};
  const a = p.invoiceAddress ?? {};
  return {
    legalName: String(p.legalName ?? '').trim(),
    orgNumber: cleanDigits(p.orgNumber),
    mvaRegistered: p.mvaRegistered === true,
    bankAccount: cleanDigits(p.bankAccount),
    invoiceAddress: {
      street: String(a.street ?? '').trim(),
      postalCode: String(a.postalCode ?? '').trim(),
      city: String(a.city ?? '').trim(),
    },
  };
}

// Feilmeldinger per felt. Tomt objekt betyr at profilen er komplett.
export function validateInvoiceProfile(profile) {
  const p = normalizeInvoiceProfile(profile);
  const errors = {};
  const tooLong = (max) => `Maks ${max} tegn`;
  if (!p.legalName) errors.legalName = 'Skriv inn firmanavn eller ditt navn';
  else if (p.legalName.length > INVOICE_PROFILE_LIMITS.legalName) errors.legalName = tooLong(INVOICE_PROFILE_LIMITS.legalName);
  if (!isValidOrgNumber(p.orgNumber)) errors.orgNumber = 'Ugyldig organisasjonsnummer (9 siffer)';
  if (!isValidBankAccount(p.bankAccount)) errors.bankAccount = 'Ugyldig kontonummer (11 siffer)';
  if (!p.invoiceAddress.street) errors.street = 'Skriv inn gateadresse';
  else if (p.invoiceAddress.street.length > INVOICE_PROFILE_LIMITS.street) errors.street = tooLong(INVOICE_PROFILE_LIMITS.street);
  if (!/^\d{4}$/.test(p.invoiceAddress.postalCode)) errors.postalCode = 'Postnummer må være fire siffer';
  if (!p.invoiceAddress.city) errors.city = 'Skriv inn poststed';
  else if (p.invoiceAddress.city.length > INVOICE_PROFILE_LIMITS.city) errors.city = tooLong(INVOICE_PROFILE_LIMITS.city);
  return errors;
}

export function isInvoiceProfileComplete(profile) {
  return Object.keys(validateInvoiceProfile(profile)).length === 0;
}

// Sant når sjåføren (brukerdokumentet) ikke har fylt ut utbetaling og fakturering.
export function isProfileIncomplete(userDoc) {
  return !isInvoiceProfileComplete(userDoc?.invoiceProfile);
}
